import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, message, Checkbox } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import ContactForm from '../components/ContactForm';
import SiteBeianRecords from '../components/SiteBeianRecords';
import { HERO_BACKGROUND_MP4 } from '../constants/publicAssets';
import '../pages/HomePage.css';
import '../components/LoginModal.css';
import './merchantRegister.css';

const { Title, Text } = Typography;

const MERCHANT_REMEMBER_KEY = 'merchantLoginRemember';

type RememberedMerchantLogin = {
  username: string;
  password: string;
};

function loadRemembered(): RememberedMerchantLogin | null {
  try {
    const raw = localStorage.getItem(MERCHANT_REMEMBER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RememberedMerchantLogin;
    if (parsed?.username && parsed?.password) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * 商家端登录：复用成员首页的视频英雄区 + precision 登录卡片样式。
 */
const MerchantLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login, token, role } = useAuth();
  const [loading, setLoading] = useState(false);
  const [heroVideoBroken, setHeroVideoBroken] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [form] = Form.useForm();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          video.style.pointerEvents = 'none';
        })
        .catch(() => {
          const handleInteraction = () => {
            video.play().catch(() => {});
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('click', handleInteraction);
          };
          document.addEventListener('touchstart', handleInteraction, { once: true });
          document.addEventListener('click', handleInteraction, { once: true });
        });
    }
  }, []);

  useEffect(() => {
    const saved = loadRemembered();
    if (saved) {
      form.setFieldsValue({
        username: saved.username,
        password: saved.password,
        rememberPassword: true
      });
    }
  }, [form]);

  if (token && role === '商家') {
    return <Navigate to="/merchant/orders" replace />;
  }

  const onFinish = async (values: {
    username: string;
    password: string;
    rememberPassword?: boolean;
  }) => {
    setLoading(true);
    try {
      const response = await fetch('/api/merchant/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: values.username,
          password: values.password
        })
      });
      const body = await response.json();
      if (!response.ok || !body.success) {
        message.error(body.error || '登录失败');
        return;
      }
      if (values.rememberPassword) {
        localStorage.setItem(
          MERCHANT_REMEMBER_KEY,
          JSON.stringify({ username: values.username, password: values.password })
        );
      } else {
        localStorage.removeItem(MERCHANT_REMEMBER_KEY);
      }
      const d = body.data;
      localStorage.setItem('merchantId', d.merchantId || '');
      localStorage.setItem('merchantName', d.merchantName || '');
      localStorage.setItem('merchantCode', d.merchantCode || '');
      login(
        d.token,
        '商家',
        d.username,
        d.realName,
        undefined,
        false,
        true,
        undefined,
        undefined,
        undefined,
        d.merchantName
      );
      message.success('登录成功');
      navigate('/merchant/orders');
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="homepage-container">
      <div className="hero-section">
        <video
          ref={videoRef}
          className="video-background"
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          onError={() => setHeroVideoBroken(true)}
          style={
            {
              pointerEvents: 'none',
              display: heroVideoBroken ? 'none' : undefined
            } as React.CSSProperties
          }
          {...({
            'webkit-playsinline': 'true',
            'x5-playsinline': 'true',
            'x5-video-player-type': 'h5',
            'x5-video-player-fullscreen': 'true'
          } as Record<string, string>)}
        >
          <source src={HERO_BACKGROUND_MP4} type="video/mp4" />
          您的浏览器不支持视频播放。
        </video>

        <div className="video-overlay" />

        <nav className="homepage-nav">
          <div className="nav-links">
            <Link to="/" className="nav-link">
              首页
            </Link>
            <button type="button" className="nav-link" onClick={() => setIsContactFormOpen(true)}>
              联系我们
            </button>
          </div>
        </nav>

        <div className="homepage-main-content">
          <div className="homepage-main-inner">
            <div className="content-center homepage-hero-text">
              <h1 className="main-headline">LabMind实验室管理系统</h1>
              <p className="sub-headline">商家端 · 订单接单与发货协同</p>
            </div>

            <div className="home-login-card-wrap" id="merchant-login-panel">
              <Card className="home-login-card home-login-card--precision" variant="borderless">
                <div className="precision-auth-surface home-precision-auth-surface">
                  <div className="login-header--precision home-login-auth-header">
                    <Title level={3} className="login-headline home-labmind-title">
                      LabMind实验室管理系统
                    </Title>
                    <Text className="login-lead">商家端登录（使用平台分享的注册账号）</Text>
                  </div>

                  <Form
                    form={form}
                    layout="vertical"
                    size="large"
                    className="login-form login-form--precision"
                    colon={false}
                    onFinish={onFinish}
                  >
                    <Form.Item
                      className="login-lm-field"
                      name="username"
                      label={<span className="login-label-block">用户名 / 邮箱</span>}
                      rules={[{ required: true, message: '请输入用户名或邮箱' }]}
                    >
                      <Input placeholder="用户名或注册邮箱" autoComplete="username" />
                    </Form.Item>
                    <Form.Item
                      className="login-lm-field"
                      name="password"
                      label={<span className="login-label-block">密码</span>}
                      rules={[{ required: true, message: '请输入密码' }]}
                    >
                      <Input.Password placeholder="请输入密码" autoComplete="current-password" />
                    </Form.Item>
                    <div className="login-remember-forgot-row">
                      <Form.Item name="rememberPassword" valuePropName="checked" noStyle>
                        <Checkbox>记住密码</Checkbox>
                      </Form.Item>
                      <span />
                    </div>
                    <Form.Item className="login-button-container">
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        size="large"
                        className="login-modal__submit"
                        icon={loading ? undefined : <ArrowRightOutlined />}
                        iconPosition="end"
                      >
                        {loading ? '登录中...' : '登录'}
                      </Button>
                    </Form.Item>
                  </Form>

                  <div className="home-auth-card-footer">
                    <Button
                      type="default"
                      block
                      size="large"
                      className="home-wechat-login-btn"
                      onClick={() => navigate('/')}
                    >
                      返回首页
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <ContactForm isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />

      <footer className="homepage-site-footer" role="contentinfo">
        <SiteBeianRecords />
      </footer>
    </div>
  );
};

export default MerchantLogin;
